// Wrapper which renders an inline media element whose source URL points at an
// AOME1-encrypted blob. A ciphertext URL cannot be handed to <img src>/<video
// src>, so the bytes are fetched, decrypted in the page and replaced with an
// object URL.
//
// Only entities that actually carry an `enc` marker are routed through here;
// legacy plaintext media keeps using the plain <img src={url}> path untouched.

import React from 'react';

import { fetchAndDecrypt } from '../lib/media-fetch.js';

// Turn 'data-src' into 'src' so it can be written into a DOMStringMap-like object.
function datasetKey(prop) {
  return prop.replace(/^data-/, '').replace(/-([a-z])/g, (_, c) => c.toUpperCase());
}

function sourceSignature(sources) {
  return (sources || []).map(s => `${s.prop}=${s.url}`).join('|');
}

export default class DecryptedMedia extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      resolved: {},
      failed: false
    };

    this._mounted = false;
    this._objectUrls = [];
    this._lazyPromise = null;

    this.handleClick = this.handleClick.bind(this);
  }

  componentDidMount() {
    this._mounted = true;
    this.resolveEager();
  }

  componentDidUpdate(prevProps) {
    if (sourceSignature(prevProps.sources) != sourceSignature(this.props.sources)) {
      this.revokeAll();
      this._lazyPromise = null;
      this.setState({resolved: {}, failed: false}, () => this.resolveEager());
    }
  }

  componentWillUnmount() {
    this._mounted = false;
    // Object URLs pin the decrypted blob in memory until revoked.
    this.revokeAll();
  }

  revokeAll() {
    this._objectUrls.forEach(url => URL.revokeObjectURL(url));
    this._objectUrls = [];
  }

  // Decrypt one source into an object URL.
  fetchSource(src) {
    return fetchAndDecrypt(src.url, src.data, {mimeType: src.mime})
      .then(blob => {
        const objectUrl = URL.createObjectURL(blob);
        if (!this._mounted) {
          // Raced with unmount: do not leak.
          URL.revokeObjectURL(objectUrl);
          return null;
        }
        this._objectUrls.push(objectUrl);
        return objectUrl;
      });
  }

  // Sources needed to render (the poster/thumbnail) are decrypted immediately.
  resolveEager() {
    const eager = (this.props.sources || []).filter(s => s.eager !== false);
    if (eager.length == 0) {
      return;
    }
    Promise.all(eager.map(s => this.fetchSource(s).then(url => [s.prop, url])))
      .then(pairs => {
        if (!this._mounted) {
          return;
        }
        const resolved = Object.assign({}, this.state.resolved);
        pairs.forEach(([prop, url]) => {
          if (url) {
            resolved[prop] = url;
          }
        });
        this.setState({resolved: resolved});
      })
      .catch(err => {
        console.warn("Failed to decrypt media:", err.message);
        if (this._mounted) {
          this.setState({failed: true});
        }
      });
  }

  // Sources only needed when the user opens the media (a full-size video) are
  // decrypted on demand, so that scrolling past an encrypted video does not
  // decrypt the whole file.
  resolveLazy() {
    if (this._lazyPromise) {
      return this._lazyPromise;
    }
    const lazy = (this.props.sources || []).filter(s => s.eager === false);
    this._lazyPromise = Promise.all(lazy.map(s => this.fetchSource(s).then(url => [s.prop, url])))
      .then(pairs => {
        const map = {};
        pairs.forEach(([prop, url]) => {
          if (url) {
            map[prop] = url;
          }
        });
        if (this._mounted) {
          this.setState({resolved: Object.assign({}, this.state.resolved, map)});
        }
        return map;
      })
      .catch(err => {
        // Allow a retry on the next click.
        this._lazyPromise = null;
        throw err;
      });
    return this._lazyPromise;
  }

  hasLazy() {
    return (this.props.sources || []).some(s => s.eager === false);
  }

  // Click handler used when a lazily-decrypted source (the video itself) is
  // required by the consumer. The downstream handlers read `e.target.src` and
  // `e.target.dataset`, so a plain object with those fields is passed on once
  // decryption completes.
  handleClick(e) {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    if (!this.props.onClick) {
      return;
    }
    const node = e && e.currentTarget ? e.currentTarget : null;
    const src = node ? node.src : this.state.resolved.src;
    const dataset = {};
    if (node && node.dataset) {
      for (const k in node.dataset) {
        dataset[k] = node.dataset[k];
      }
    }

    this.resolveLazy()
      .then(map => {
        if (!this._mounted) {
          return;
        }
        Object.keys(map).forEach(prop => {
          if (prop == 'src') {
            return;
          }
          dataset[datasetKey(prop)] = map[prop];
        });
        this.props.onClick({
          preventDefault: () => {},
          target: {
            src: map.src || src,
            dataset: dataset
          }
        });
      })
      .catch(err => {
        console.warn("Failed to decrypt media:", err.message);
        if (this.props.onError) {
          this.props.onError("Failed to decrypt media: " + err.message, 'err');
        }
      });
  }

  render() {
    const {element, sources, placeholder, broken, onClick, onError, ...rest} = this.props;
    const props = Object.assign({}, rest);
    const resolved = this.state.resolved;

    (sources || []).forEach(s => {
      if (resolved[s.prop]) {
        props[s.prop] = resolved[s.prop];
      } else {
        // Never leave a ciphertext URL in the DOM.
        delete props[s.prop];
      }
    });

    if (this.state.failed) {
      if (broken) {
        props.src = broken;
      }
    } else if (!props.src && placeholder) {
      props.src = placeholder;
    }

    if (onClick) {
      props.onClick = this.hasLazy() ? this.handleClick : onClick;
    }

    return React.createElement(element, props);
  }
};
